import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { QuestionnaireTemplateVersionByIdResponse } from '@risksmart-app/trpc/src/types';
import type { GetLatestQuestionnaireTemplateVersionQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

const mapTrpcQuestionnaireTemplateVersionToGraphQL = (
  trpcData: QuestionnaireTemplateVersionByIdResponse | undefined
): GetLatestQuestionnaireTemplateVersionQuery => {
  return {
    questionnaire_template_version: trpcData?.questionnaire_template_version
      ? [trpcData?.questionnaire_template_version]
      : [],
  };
};

export const useGetLatestQuestionnaireTemplateVersionTRPC = (
  parentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.questionnaireTemplateVersion.getLatest.queryOptions({
      parentId,
    }),

    enabled: trpcEnabled && !skip, // Only enable when TRPC flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcQuestionnaireTemplateVersionToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
